import PageHeader from "../Components/PageHeader";
import Section from "../Components/Section";
import Card from "../Components/Card";
import { FaRegLightbulb, FaRegHandshake } from "react-icons/fa6";
import { LuSettings } from "react-icons/lu";
import { FaPeopleGroup } from "react-icons/fa6";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";

export default function Electrical() {
  useChangeTitle("Electrical Services");

  const electricalFeatures = [
    {
      index: "1",
      title: "Safety First",
      desc: "All electrical installations prioritize safety for both human and equipment following international safety standards.",
      icon: <FaRegLightbulb />,
    },
    {
      index: "2",
      title: "Quality Materials",
      desc: "High-quality cables, MCC panels, and lighting fixtures from trusted manufacturers for reliable performance.",
      icon: <FaRegHandshake />,
    },
    {
      index: "3",
      title: "Code Compliance",
      desc: "Installations based on local and international codes including IEC, NEC, and NEMA standards.",
      icon: <LuSettings />,
    },
    {
      index: "4",
      title: "Thermal Imaging",
      desc: "Optional thermal imaging for high-load MCC panels to ensure proper installation and operation.",
      icon: <FaPeopleGroup />,
    },
  ];

  return (
    <>
      <PageHeader heading="Electrical Systems" />
      
      {/* Hero Section with Dynamic Background */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/services/electrical.jpeg" 
            alt="Electrical Systems" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-900/90 via-orange-800/80 to-yellow-900/90"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <div className="animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              Professional
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Electrical Systems
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-yellow-100 max-w-4xl mx-auto leading-relaxed">
              Powering progress with cutting-edge electrical infrastructure, intelligent lighting solutions, 
              and comprehensive safety systems engineered to international standards.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-2xl">
                Get Electrical Quote
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-orange-800 px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300">
                Safety Consultation
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Services Overview with Enhanced Design */}
      <Section 
        title="Our Electrical Services" 
        desc="Comprehensive electrical solutions engineered for reliability, safety, and efficiency"
        className="py-24 bg-gradient-to-br from-white via-yellow-50 to-white relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-32 h-32 bg-yellow-100 rounded-full -translate-x-16 -translate-y-16 opacity-50"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-orange-200 rounded-full translate-x-24 translate-y-24 opacity-30"></div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          {[
            { 
              title: "Power Distribution", 
              desc: "Complete electrical power distribution systems with proper load calculations and circuit protection.",
              icon: "⚡",
              color: "from-yellow-500 to-orange-500"
            },
            { 
              title: "Lighting Systems", 
              desc: "Energy-efficient lighting solutions including LED systems, controls, and emergency lighting.",
              icon: "💡",
              color: "from-orange-500 to-red-500"
            },
            { 
              title: "Grounding & Earthing", 
              desc: "Proper grounding systems for electrical safety and equipment protection against surges.",
              icon: "🌍",
              color: "from-green-500 to-teal-500"
            },
            { 
              title: "Panel Installation", 
              desc: "MCC panels, distribution boards, and control panels with thermal imaging verification.",
              icon: "🔧",
              color: "from-blue-500 to-purple-500"
            },
            { 
              title: "Cable Management", 
              desc: "Professional cable installation with proper routing, support, and protection systems.",
              icon: "📡",
              color: "from-purple-500 to-pink-500"
            },
            { 
              title: "Emergency Systems", 
              desc: "Emergency power systems, UPS installations, and backup generator connections.",
              icon: "🚨",
              color: "from-red-500 to-orange-500"
            }
          ].map((service, index) => (
            <div key={index} className="group relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 rounded-2xl blur opacity-0 group-hover:opacity-25 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${service.color} rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300`}>
                      <span className="text-white text-xl">{service.icon}</span>
                    </div>
                    <h3 className="font-bold text-xl text-gray-800 group-hover:text-orange-600 transition-colors">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{service.desc}</p>
                </div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-yellow-50 rounded-tl-full opacity-50"></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Electrical Standards Section */}
      <div className="py-20 bg-gradient-to-br from-gray-900 via-orange-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 border border-yellow-400 rounded-full opacity-20"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 border border-orange-400 rounded-full opacity-30"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-yellow-500 rounded-full opacity-10"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">International Electrical Standards</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our electrical installations adhere to the highest international safety and performance standards, 
              ensuring reliable and efficient power systems.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "IEC", desc: "International Electrotechnical Commission", icon: "⚡" },
              { name: "NEC", desc: "National Electrical Code Standards", icon: "🔌" },
              { name: "NEMA", desc: "National Electrical Manufacturers", icon: "⚙️" }
            ].map((standard, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:scale-105">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                    <span className="text-2xl">{standard.icon}</span>
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-white">{standard.name}</h3>
                  <p className="text-gray-300 text-sm">{standard.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <Section 
        title="Why Choose Our Electrical Services" 
        className="py-16 bg-white"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {electricalFeatures.map((feature) => (
            <Card
              key={feature.index}
              index={feature.index}
              icon={feature.icon}
              title={feature.title}
              desc={feature.desc}
            />
          ))}
        </div>
      </Section>

      {/* Project Gallery with Modern Design */}
      <Section 
        title="Our Electrical Projects" 
        desc="Showcasing excellence in electrical engineering across diverse industries"
        className="py-24 bg-gradient-to-br from-gray-900 via-orange-900 to-gray-900 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-2 h-32 bg-yellow-400 opacity-20"></div>
          <div className="absolute top-40 right-20 w-2 h-24 bg-orange-500 opacity-30"></div>
          <div className="absolute bottom-20 left-32 w-2 h-28 bg-yellow-300 opacity-25"></div>
        </div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {[
            {
              image: "/images/home/elec home.JPG",
              title: "Commercial Buildings",
              desc: "Complete Electrical Systems",
              gradient: "from-yellow-500/80 to-orange-600/80"
            },
            {
              image: "/images/home/electrical-systems.jpg",
              title: "Industrial Facilities",
              desc: "Power Distribution & Control",
              gradient: "from-orange-500/80 to-red-600/80"
            },
            {
              image: "/images/services/electrical.jpeg",
              title: "Panel Installations",
              desc: "MCC & Distribution Panels",
              gradient: "from-red-500/80 to-orange-600/80"
            }
          ].map((project, index) => (
            <div key={index} className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${project.gradient} opacity-0 group-hover:opacity-90 transition-opacity duration-500`}></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h4 className="text-2xl font-bold mb-2 group-hover:text-yellow-300 transition-colors">
                    {project.title}
                  </h4>
                  <p className="text-gray-200 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                    {project.desc}
                  </p>
                  <div className="w-16 h-1 bg-gradient-to-r from-yellow-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-300"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
} 